/**
 * Web "Sign in with Apple" helper.
 *
 * Lazy-loads Apple's appleid.auth.js, initializes it with the configured
 * Service ID + return URL, and exposes a single `signInWithApple()` that
 * resolves to an identity token. The web LoginPage POSTs the token to the
 * shared /api/auth/apple-sso endpoint that also serves the native shell.
 *
 * Config (Vite env vars, build-time):
 *   VITE_APPLE_SERVICE_ID — the Services ID from the Apple Developer portal
 *                          (e.g. com.packetprospector.field.web). Distinct
 *                          from the iOS App ID.
 *   VITE_APPLE_REDIRECT_URI — the form-post return URL configured on the
 *                          Service ID (e.g. https://app.packetprospector.com/auth/apple/callback).
 *                          The popup posts here but we intercept it; the
 *                          backend doesn't need an endpoint at that URL.
 *
 * If either env var is missing, isAppleSsoConfigured() returns false and the
 * LoginPage hides the Apple button — useful in dev where the Service ID
 * hasn't been provisioned yet.
 */

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init(options: AppleAuthInitOptions): void;
        signIn(options?: AppleAuthSignInOptions): Promise<AppleAuthResponse>;
      };
    };
  }
}

interface AppleAuthInitOptions {
  clientId: string;
  scope: string;
  redirectURI: string;
  state?: string;
  nonce?: string;
  usePopup: boolean;
}

interface AppleAuthSignInOptions {
  state?: string;
  nonce?: string;
}

interface AppleAuthResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  user?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
    email?: string;
  };
}

const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

let scriptLoadPromise: Promise<void> | null = null;
let initialized = false;

export function getAppleServiceId(): string | undefined {
  return import.meta.env.VITE_APPLE_SERVICE_ID;
}

export function getAppleRedirectUri(): string | undefined {
  return import.meta.env.VITE_APPLE_REDIRECT_URI;
}

export function isAppleSsoConfigured(): boolean {
  return Boolean(getAppleServiceId() && getAppleRedirectUri());
}

function loadAppleScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    if (window.AppleID) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${APPLE_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Apple SDK load failed')), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Apple SDK load failed'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

function generateNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Apple's web JS SDK does NOT hash the nonce when passed via auth.signIn().
 * It embeds it verbatim in the id_token's `nonce` claim. The backend, however,
 * follows Apple's recommended pattern and compares SHA256(rawNonce) against
 * the token's nonce. To make those agree, we hash here, hand the hash to
 * Apple, and return the raw nonce so the caller can pass it to the backend.
 */
async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Open Apple's web Sign-In popup, return the raw response (identity token +
 * any user info Apple chose to share on this attempt). Caller is responsible
 * for posting the token to the backend.
 *
 * Throws if Apple SSO isn't configured (missing env vars), if the SDK
 * fails to load, or if the user cancels the popup.
 */
export async function signInWithApple(): Promise<{
  identityToken: string;
  nonce: string;
  firstName?: string;
  lastName?: string;
}> {
  if (!isAppleSsoConfigured()) {
    throw new Error('Sign in with Apple is not configured for this environment.');
  }

  await loadAppleScript();
  if (!window.AppleID) {
    throw new Error('Apple SDK loaded but window.AppleID is undefined');
  }

  const rawNonce = generateNonce();
  const hashedNonce = await sha256Hex(rawNonce);
  if (!initialized) {
    window.AppleID.auth.init({
      clientId: getAppleServiceId()!,
      scope: 'name email',
      redirectURI: getAppleRedirectUri()!,
      usePopup: true,
    });
    initialized = true;
  }

  // Pass the HASHED nonce to Apple — it ends up in the JWT verbatim. The
  // backend recomputes SHA256(rawNonce) and compares to that claim, so we
  // hand the RAW nonce back to the caller.
  const response = await window.AppleID.auth.signIn({ nonce: hashedNonce });
  return {
    identityToken: response.authorization.id_token,
    nonce: rawNonce,
    firstName: response.user?.name?.firstName,
    lastName: response.user?.name?.lastName,
  };
}
